import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { formatNumToStandardNotation } from '@modules/common/utils';
import { ShowFeedbackDrawerFromTableOptions } from '@modules/quantify/components/QuantifyReportTableWidget/types';
import { getCompareFieldHeaderTooltipTitle } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers';
import { getTooltipTitleForFormulaValueCell } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/getTooltipTitleForFormulaValueCell';
import { renderCompareAbsoluteDiffCell } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/renderCompareAbsoluteDiffCell';
import { renderCompareFieldColumnCell } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/renderCompareFeedbackColumnCell';
import { renderCompareFieldValueHeader } from '@modules/quantify/hooks/useGetQuantifyReportTableColumns/helpers/renderCompareFieldValueHeader';
import { QuantifyQuery } from '@modules/quantify/store/types';
import { isAggregateFormula } from '@modules/quantify/utils';
import { Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { EqlField } from 'generated/graphql';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getCompareColumnsParams {
  compareTo: QuantifyQuery['compareQuery'];
  groupBy: QuantifyQuery['groupByField'];
  onFeedbackCountClick: (params: ShowFeedbackDrawerFromTableOptions) => void;
  colors: {
    colorA: string;
    colorB: string;
  };
  totalFeedbackCount?: {
    fieldA: number;
    fieldB: number;
  };
  compareLabelPrefix: string;
  compareFieldNames: {
    fieldAName: string;
    fieldBName: string;
  };
  formulaID?: EQLFunctionName;
  shouldShowCompareFieldHeaderTooltip?: boolean;
  isCompactTableVariant?: boolean;
  measuredAsCriteria: EqlField | undefined;
  shouldRemoveFileUploadPrefix: boolean;
  isUnifiedFieldEnabled: boolean;
  isSingleRecordQuery: boolean;
}

export const getCompareColumns = ({
  compareTo,
  groupBy,
  onFeedbackCountClick,
  colors: { colorA, colorB },
  totalFeedbackCount,
  compareLabelPrefix,
  compareFieldNames: { fieldAName, fieldBName },
  formulaID,
  shouldShowCompareFieldHeaderTooltip,
  isCompactTableVariant,
  measuredAsCriteria,
  shouldRemoveFileUploadPrefix,
  isUnifiedFieldEnabled,
  isSingleRecordQuery,
}: getCompareColumnsParams): GridColDef[] => {
  return [
    {
      field: 'currValue',
      headerName: 'Current Value',
      minWidth: isCompactTableVariant ? 110 : 180,
      flex: 1,
      type: 'number',
      renderHeader: () => {
        return renderCompareFieldValueHeader({
          compareLabelPrefix,
          color: colorA,
          compareLabel: fieldAName,
          totalFeedbackCount: totalFeedbackCount?.fieldA,
          shouldBreakInTwoLines: !isAggregateFormula(formulaID),
          tooltipTitle: shouldShowCompareFieldHeaderTooltip
            ? getCompareFieldHeaderTooltipTitle({
                compareTo,
                compareLabel: fieldAName,
                field: 'A',
                color: colorA,
              })
            : undefined,
        });
      },
      renderCell: (params) =>
        renderCompareFieldColumnCell({
          params,
          onFeedbackCountClick,
          isCompareFieldA: true,
          skipDataPointFilter: isSingleRecordQuery,
          tooltipTitle: getTooltipTitleForFormulaValueCell({
            compareTo,
            displayName: params.row.label,
            feedbackCount: params.value,
            groupBy,
            isCompareFieldA: true,
            params,
            formulaID,
            measuredAsCriteria,
            shouldRemoveFileUploadPrefix,
            isUnifiedFieldEnabled,
          }),
        }),
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'prevValue',
      headerName: 'Prev Value',
      minWidth: isCompactTableVariant ? 110 : 180,
      flex: 1,
      type: 'number',
      renderHeader: () => {
        return renderCompareFieldValueHeader({
          compareLabelPrefix,
          color: colorB,
          compareLabel: fieldBName,
          totalFeedbackCount: totalFeedbackCount?.fieldB,
          shouldBreakInTwoLines: !isAggregateFormula(formulaID),
          tooltipTitle: shouldShowCompareFieldHeaderTooltip
            ? getCompareFieldHeaderTooltipTitle({
                compareTo,
                compareLabel: fieldBName,
                field: 'B',
                color: colorB,
              })
            : undefined,
        });
      },
      renderCell: (params) =>
        renderCompareFieldColumnCell({
          params,
          onFeedbackCountClick,
          isCompareFieldA: false,
          skipDataPointFilter: isSingleRecordQuery,
          tooltipTitle: getTooltipTitleForFormulaValueCell({
            compareTo,
            displayName: params.row.label,
            feedbackCount: params.value,
            groupBy,
            isCompareFieldA: false,
            params,
            formulaID,
            measuredAsCriteria,
            shouldRemoveFileUploadPrefix,
            isUnifiedFieldEnabled,
          }),
        }),
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'absoluteDelta',
      headerName: 'Difference',
      renderHeader: () => {
        return (
          <Tooltip title="Difference" disableInteractive>
            <TypographyWithEllipsis variant="subtitle2">
              Difference
            </TypographyWithEllipsis>
          </Tooltip>
        );
      },
      width: 100,
      minWidth: 80,
      type: 'number',
      valueFormatter: ({ value }) =>
        value > 0
          ? `+${formatNumToStandardNotation(value)}`
          : formatNumToStandardNotation(value),
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'absValueDelta',
      headerName: 'Absolute Difference',
      renderHeader: () => {
        if (isCompactTableVariant) {
          return (
            <Tooltip title="Absolute Difference" disableInteractive>
              <Stack
                lineHeight="18px"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <TypographyWithEllipsis
                  variant="subtitle2"
                  lineHeight="inherit"
                  shouldShowTooltip={false}
                >
                  Absolute
                </TypographyWithEllipsis>
                <TypographyWithEllipsis
                  variant="subtitle2"
                  lineHeight="inherit"
                  shouldShowTooltip={false}
                >
                  Difference
                </TypographyWithEllipsis>
              </Stack>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Absolute Difference" disableInteractive>
            <TypographyWithEllipsis variant="subtitle2">
              Absolute Difference
            </TypographyWithEllipsis>
          </Tooltip>
        );
      },
      width: 150,
      minWidth: 80,
      type: 'number',
      sortingOrder: ['asc', 'desc'],
      renderCell: (params) =>
        renderCompareAbsoluteDiffCell({
          params,
        }),
    },
  ];
};
